import React, { useState, useEffect } from "react";
import { ErrorType } from "../../types/common-type";
import handleClearError from "../../core/methods/handleClearError";
import MultiSearchableSelect from "../../core/common/MultiSearchableSelect";
import { useOrganizations } from "../../core/customhook/hooks/useOrganizations";
import { useProjectsByMultiOrgId } from "../../core/customhook/hooks/projectHooks";
import { useLocationByMultiProjectId } from "../../core/customhook/hooks/locationHooks";
import { useMultiPrograms } from "../../core/customhook/hooks/programHooks";
import { useDepartmentByMultiProgramId } from "../../core/customhook/hooks/departmentHooks";
import { useUserByOrg } from "../../core/customhook/hooks/userHooks";

export type SearchFormDataType = {
  case_id: string;
  applicant_name: string;
  email: string;
  from_date: string;
  to_date: string;
};
 
export interface FiltersSelectType {
  organization: string[];
  project: string[];
  location: string[];
  program: string[];
  department?: string[];
  users: string[];
}

export type SearchPayloadType = FiltersSelectType & SearchFormDataType;

interface FilterProps {
  filterData: any;
  searchFilter: any;
  setSearchFilter: React.Dispatch<React.SetStateAction<any>>;
  setFilterData: React.Dispatch<React.SetStateAction<any>>;
  onSearch?: (payload: SearchPayloadType) => void;
  storageKey?: string;
}

const emptyFilterData: FiltersSelectType = {
  organization: [],
  project: [],
  location: [],
  program: [],
  department: [],
  users: [],
};

const emptySearchFilter: SearchFormDataType = {
  case_id: "",
  applicant_name: "",
  email: "",
  from_date: "",
  to_date: "",
};

const FiltersTrackCase: React.FC<FilterProps> = ({
  filterData,
  searchFilter,
  setSearchFilter,
  setFilterData,
  onSearch,
  storageKey,
}) => {
  const [errors, setErrors] = useState<ErrorType>({});
 
  const FULL_STORAGE_KEY = `activeTabFilter-${storageKey}`;

  const { options: organizationOption, loading: loadingOrg } =
    useOrganizations();

  const { options: projectOptions, loading: loadingProject } =
    useProjectsByMultiOrgId(filterData?.organization);

  const { options: locationOptions, loading: loadingLocation } =
    useLocationByMultiProjectId(filterData?.project);

  const { options: programOptions, loading: loadingProgram } = useMultiPrograms(
    filterData?.project,
    filterData?.location,
  );
  const { options: department, loading: loadingDepartment } =
    useDepartmentByMultiProgramId(filterData?.program ?? []);
  const { options: users, loading: loadingUsers } = useUserByOrg(
    filterData?.organization ?? [],
  );

  useEffect(() => {
    try {
      const saved = localStorage.getItem(FULL_STORAGE_KEY);
      if (!saved) return;

      const parsed = JSON.parse(saved);
      if (parsed?.filterData) setFilterData(parsed.filterData);
      if (parsed?.searchFilter) setSearchFilter(parsed.searchFilter);
    } catch (err) {
      console.error("Failed to restore saved filters from localStorage:", err);
    }
  }, [FULL_STORAGE_KEY]);

  const validateFilters = () => {
    const newErrors: ErrorType = {};

    if (!filterData?.organization?.length) {
      newErrors.organization = "Organization is required";
    }

    if (!filterData?.project?.length) {
      newErrors.project = "Account is required";
    } 
    return newErrors;
  };
 
  const handleSelectDataChange = (name: string, values: string[]) => {
    setFilterData((prev: any) => ({ ...prev, [name]: values }));
    handleClearError(setErrors, name);
  };

  const handleSearch = () => {
    const selectErrors = validateFilters();
    setErrors(selectErrors);
    if (Object.keys(selectErrors).length > 0) return;
    const payload: SearchPayloadType = {
      ...filterData,
      ...searchFilter,
    };


    try {
      localStorage.setItem(
        FULL_STORAGE_KEY,
        JSON.stringify({ filterData, searchFilter }),
      );
    } catch (err) {
      console.error("Failed to save filters to localStorage:", err);
    }

    onSearch?.(payload);
  };

  const handleClearAll = () => {
    setFilterData({ ...emptyFilterData });
    setSearchFilter({ ...emptySearchFilter });
    setErrors({}); 
    try {
      localStorage.removeItem(FULL_STORAGE_KEY);
    } catch (err) {
      console.error("Failed to clear saved filters from localStorage:", err);
    }
  };

  const auth = localStorage.getItem("auth");
  const profileId = auth ? JSON.parse(auth).profileId : null;

  return (
    <div className="py-3"> 
      <div className="row mb-0 gx-2 gy-0 "> 
        <div className="col-6 col-sm-4 col-md-3 col-lg-2 mb-3 ">
          <MultiSearchableSelect
            label="Organization"
            name="organization"
            value={filterData?.organization ?? []}
            options={organizationOption ?? []}
            onChange={handleSelectDataChange}
            error={errors?.organization}
            loading={loadingOrg}
            required
          />
        </div>

        {/* Mandatory */}
        <div className="col-6 col-sm-4 col-md-3 col-lg-2 mb-3 ">
          <MultiSearchableSelect
            label="Account"
            name="project"
            value={filterData?.project ?? []}
            options={projectOptions ?? []}
            onChange={handleSelectDataChange}
            error={errors?.project}
            loading={loadingProject}
            required
          />
        </div>

        {/* Optional */}
        <div className="col-6 col-sm-4 col-md-3 col-lg-2 mb-3 ">
          <MultiSearchableSelect
            label="Location"
            name="location"
            value={filterData?.location ?? []}
            options={locationOptions ?? []}
            onChange={handleSelectDataChange}
            // error={errors?.location}
            loading={loadingLocation} 
          />
        </div>

        {/* Mandatory */}
        <div className="col-6 col-sm-4 col-md-3 col-lg-2 mb-3 ">
          <MultiSearchableSelect
            label="Program"
            name="program"
            value={filterData?.program ?? []}
            options={programOptions ?? []}
            onChange={handleSelectDataChange}
            // error={errors?.program}
            loading={loadingProgram} 
          />
        </div>

        {/* Optional */}
        <div className="col-6 col-sm-4 col-md-3 col-lg-2 mb-3 ">
          <MultiSearchableSelect
            label="Department"
            name="department"
            value={filterData?.department ?? []}
            options={department ?? []}
            onChange={handleSelectDataChange}
            // loading={loadingDepartment}
            error={errors?.department} 
          />
        </div>

        {/* Optional */}
        {profileId !== 4 && (
          <div className="col-6 col-sm-4 col-md-3 col-lg-2 mb-3">
            <MultiSearchableSelect
              label="Assigned User"
              name="users"
              value={filterData?.users ?? []}
              options={users ?? []}
              loading={loadingUsers}
              onChange={handleSelectDataChange}
            />
          </div>
        )}

        <div
          className={`col-6 col-sm-4 col-md-12 col-lg-12 mb-3 d-flex justify-content-end`}
        >
          <div className="d-flex justify-content-end gap-3 w-25">
            <button
              className="btn btn-primary danger w-50 py-2"
              onClick={handleSearch}
            >
              Search
            </button>
            <button
              className="btn btn-secondary w-50 py-2"
              style={{ minWidth: "max-content" }}
              onClick={handleClearAll}
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FiltersTrackCase; 